import React, { useCallback } from 'react';
import { FaLink, FaLinkSlash } from 'react-icons/fa6';
import { useParams } from 'react-router-dom';
import { Text } from 'rizzui';
import { toast } from 'sonner';
import CustomPopover from '../../../components/buttons/CustomPopover';
import WidgetCard from '../../../components/card/WidgetCard';
import CustomTable from '../../../components/table/CustomTable';
import useFetch from '../../../hooks/useFetch';
import useFilters from '../../../hooks/useFilters';
import useHandleErrors from '../../../hooks/useHandleErrors';
import { RankingService } from '../../../services/ranking/rankingService';
import { UsersApiResponse } from '../../../type/entities/user-type';

const addedUserColumnOrder = [
  'id',
  'userId',
  'userName',
  'userLastName',
  'commercialNetworkName',
  'departmentName',
];

const allUserColumnOrder = [
  'id',
  'name',
  'lastName',
  'commercialNetwork',
  'department',
];

export const RankingInfo: React.FC = () => {

  const { id = '' } = useParams<{ id: string }>();
  const { filters: allUsersFilters, updateFilters: updateAllUsersFilters, updateFilterOrder: updateAllUsersFilterOrder, updatePage: updateAllUsersPage, updatePageSize: updateAllUsersPageSize } = useFilters({ non_appear_on_ranking: id });
  const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize } = useFilters({ ranking: id });
  const { handleErrors } = useHandleErrors();
  const service = new RankingService();

  const [data] = useFetch(useCallback(async () => {
    const response = await service.getRankingById(id);
    return response.getResponseData();
  }, []));

  // Fetch de todos los usuarios disponibles
  const [allUsersData, allUsersLoading, errorAllUsers, refetchAllUsers] = useFetch(useCallback(async () => {
    const response = await service.listUnlinkedUsers(allUsersFilters);
    return response.getResponseData() as UsersApiResponse;
  }, [allUsersFilters]));

  const [addedUsersData, addedUsersLoading, errorAddedUsers, refetchAddedUsers] = useFetch(useCallback(async () => {
    const response = await service.listRankingUsers(filters);
    return response.getResponseData() as UsersApiResponse;
  }, [filters]));

  //-------------------------------------------------------------------------------------------------------------------------------
  /**
   * @ES DESVINCULAR USUARIOS DEL RANKING
   * @EN UNLINK USERS FROM RANKING
   */
  //-------------------------------------------------------------------------------------------------------------------------------
  const handleRemoveUsers = async (selectedRows: string[]) => {
    const response = await (await service.removeUsersFromRanking({ userIds: selectedRows, rankingId: id })).getResponseData();
    if (response.success) {
      toast.success(response.message);
      refetchAddedUsers();
      refetchAllUsers();
    } else {
      handleErrors(response);
    }
  };
  //-------------------------------------------------------------------------------------------------------------------------------

  //-------------------------------------------------------------------------------------------------------------------------------
  /**
   * @ES AGREGAR USUARIOS AL RANKING
   * @EN ADD USERS TO RANKING
   */
  //-------------------------------------------------------------------------------------------------------------------------------
  const handleAddUsers = async (selectedRows: string[]) => {
    const response = await (await service.addUsersToRanking({ userIds: selectedRows, rankingId: id })).getResponseData();
    if (response.success) {
      toast.success(response.message);
      refetchAddedUsers();
      refetchAllUsers();
    } else {
      handleErrors(response);
    }
  };
  //-------------------------------------------------------------------------------------------------------------------------------

  const orderedAddedUsersData = (data: any[]) => data?.map((row: any) => {
    const orderedRow: any = {};
    addedUserColumnOrder.forEach((key) => { orderedRow[key] = row[key]; });
    return orderedRow;
  });

  const orderedAllUsersData = () => allUsersData?.data.map((row: any) => {
    const orderedRow: any = {};
    allUserColumnOrder.forEach((key) => { orderedRow[key] = row[key]; });
    return orderedRow;
  });

  return (
    <WidgetCard title={
      <div className="flex items-center gap-2">
        <span>Ranking {data?.name}</span>
        <div className="flex items-center ms-3">
          {data?.active ? <span className='text-sm font-medium text-green-600'>Activo</span> : <span className='text-sm font-medium text-red-600'>Desactivado</span>}
          <div
            className={`w-3 h-3 ms-1 rounded-full ${data?.active ? 'bg-green-500' : 'bg-red-500'}`}
            title={data?.active ? 'Activo' : 'Desactivado'}
          />
        </div>
      </div>
    }>
      {data?.description && <span className='text-sm text-gray-600'>{data.description}</span>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6">
        {/* Added Users List */}
        <div className='rounded p-4 border border-gray-200 bg-white gap-4'>
          <Text className='heading-6 font-semibold text-gray-700 mb-6'>Usuarios Agregados al Ranking ({addedUsersData?.totalRegisters})</Text>
          <CustomTable
            id={'ranking-added-users-table'}
            largeTable
            columnOrder={addedUserColumnOrder}
            data={addedUsersData ? orderedAddedUsersData(addedUsersData?.data) : []}
            columnsNotShown={['userId', 'userLastName']}
            isLoading={addedUsersLoading}
            overrideColumns={[
              {
                key: 'userName',
                render: (row: any) => {
                  const user = row.row.original;
                  return `${user.userName} ${user.userLastName || ''}`;
                }
              },
            ]}
            multiActions={[
              {
                label: 'Desvincular usuarios',
                permissions: { group: 'rankings', permission: 'edit_rankings' },
                render: (selectedRows: any) => {
                  return (
                    <CustomPopover
                      title='Desvincular usuarios'
                      tooltipContent={'Desvincular usuarios del ranking'}
                      description='¿Estás seguro de desvincular los elementos seleccionados?'
                      onClick={() => { handleRemoveUsers(addedUsersData?.data?.filter((u: any) => selectedRows.includes(u.id)).map((user: any) => user.userId)) }}
                      icon={<FaLinkSlash className="h-4 w-4" size={20} />}
                      className="h-auto w-auto p-1"
                    />
                  )
                },
              },
            ]}
            filters={filters}
            updateFilters={updateFilters}
            updateFilterOrder={updateFilterOrder}
            paginationData={addedUsersData ? {
              pageSize: filters.limit,
              currentPage: filters.page,
              pageCount: addedUsersData.lastPage,
              totalCount: addedUsersData.totalRegisters,
              handlePagination: updatePage,
              handlePerPage: updatePageSize,
            } : undefined}
            filter={false}
          />
        </div>

        {/* Available Users List */}
        <div className='rounded p-4 border border-gray-200 bg-white gap-4'>
          <Text className='heading-6 font-semibold text-gray-700 mb-6'>Usuarios Disponibles ({allUsersData?.totalRegisters || 0})</Text>
          <CustomTable
            id={'ranking-available-users-table'}
            largeTable
            columnOrder={allUserColumnOrder}
            data={orderedAllUsersData()}
            columnsNotShown={['lastName']}
            isLoading={allUsersLoading}
            overrideColumns={[
              {
                key: 'name',
                render: (row: any) => {
                  const user = row.row.original;
                  return `${user.name} ${user.lastName || ''}`;
                }
              },
              {
                key: 'commercialNetwork',
                render: (row: any) => {
                  return row.row.original.commercialNetwork ? row.row.original.commercialNetwork.name : 'N/A';
                }
              },
              {
                key: 'department',
                render: (row: any) => {
                  return row.row.original.department ? row.row.original.department.name : 'N/A';
                }
              }
            ]}
            multiActions={[
              {
                label: 'Agregar usuarios',
                permissions: { group: 'rankings', permission: 'edit_rankings' },
                render: (selectedRows: any) => {
                  return (
                    <CustomPopover
                      title='Agregar usuarios'
                      tooltipContent={'Agregar usuarios al ranking'}
                      description='¿Estás seguro de agregar los elementos seleccionados?'
                      onClick={() => { handleAddUsers(selectedRows) }}
                      icon={<FaLink className="h-4 w-4" size={20} />}
                      className="h-auto w-auto p-1"
                      confirmColor='primary'
                    />
                  )
                },
              },
            ]}
            filters={allUsersFilters}
            updateFilters={updateAllUsersFilters}
            updateFilterOrder={updateAllUsersFilterOrder}
            paginationData={allUsersData ? {
              pageSize: allUsersFilters.limit,
              currentPage: allUsersFilters.page,
              pageCount: allUsersData.lastPage,
              totalCount: allUsersData.totalRegisters,
              handlePagination: updateAllUsersPage,
              handlePerPage: updateAllUsersPageSize,
            } : undefined}
            filter={false}
          />
        </div>
      </div>
    </WidgetCard>
  );
};

export default RankingInfo;